import { useEffect, useState } from "preact/hooks";
import { parseNumber } from "../app/format";
import { formatGrind, grindInputError, parseGrind } from "../app/grindFormat";
import type { Grinder, Shot, Taste } from "../engine/model";
import { Modal } from "./dialogs";
import { Stars } from "./Stars";
import { TasteControl } from "./TasteControl";

/**
 * Edit a logged shot's values. Timestamp, notes and the stamped prediction fields are kept. Notes are no longer
 * entered when logging; older shots that have some show them read-only.
 */
export function ShotEditDialog(props: { shot: Shot | null; grinder: Grinder | null; onClose: () => void; onSave: (shot: Shot) => Promise<string | null> }) {
  const s = props.shot;
  const [dose, setDose] = useState("");
  const [yieldG, setYield] = useState("");
  const [time, setTime] = useState("");
  const [grind, setGrind] = useState("");
  const [rating, setRating] = useState(0);
  const [taste, setTaste] = useState<Taste | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!s) return;
    setDose(String(s.doseG)); setYield(String(s.yieldG)); setTime(String(s.timeSeconds)); setGrind(props.grinder ? formatGrind(s.grindSetting, props.grinder) : String(s.grindSetting));
    setRating(s.rating ?? 0); setTaste(s.taste); setError("");
  }, [s?.id]);

  const save = async () => {
    if (!s) return;
    const d = parseNumber(dose), y = parseNumber(yieldG), t = parseNumber(time), g = parseGrind(grind, props.grinder);
    if (d === null || y === null || t === null || grind.trim() === "") { setError("All four numbers are required"); return; }
    if (g === null) { setError(grindInputError(props.grinder)); return; }
    const problem = await props.onSave({ ...s, doseG: d, yieldG: y, timeSeconds: t, grindSetting: g,
      rating: rating >= 1 && rating <= 5 ? rating : null, taste });
    if (problem) setError(problem); else props.onClose();
  };

  const field = (label: string, value: string, set: (v: string) => void, text = false) => (
    <label class="field">{label}
      <input type="text" inputMode={text ? "text" : "decimal"} autocapitalize={text ? "characters" : "off"} value={value} onInput={(e) => set((e.currentTarget as HTMLInputElement).value)} />
    </label>
  );

  return (
    <Modal open={s !== null} onClose={props.onClose}>
      <h2>Edit shot</h2>
      {s && <div class="hint">Logged {s.timestamp.replace("T", " ").slice(0, 16)}. The recommendation shown at the time stays recorded.</div>}
      <div class="grid2">
        {field("Dose (g)", dose, setDose)}
        {field("Yield (g)", yieldG, setYield)}
        {field("Time (s)", time, setTime)}
        {field("Grind setting", grind, setGrind, props.grinder?.dialFormat === "macro_micro")}
      </div>
      <Stars value={rating} onChange={setRating} />
      <TasteControl value={taste} onToggle={(t) => setTaste(taste === t ? null : t)} />
      {s && s.notes.trim() !== "" && (
        <div class="field" data-testid="shot-notes">Notes
          <div class="readonly-notes">{s.notes}</div>
        </div>
      )}
      {error && <div class="error">{error}</div>}
      <div class="actions">
        <button class="secondary" type="button" onClick={props.onClose}>Cancel</button>
        <button type="button" onClick={() => void save()}>Save changes</button>
      </div>
    </Modal>
  );
}
